import { useEffect, useRef } from "react"
import nProgress from "nprogress"

import { containsAuthPath } from "@/lib/auth-utils"
import { refreshAuthToken } from "@/lib/auth/client"
import { authLoopDetector } from "@/lib/auth/loop-detector"
import { mtLocation } from "@/lib/mt-location"
import { useRouter } from "@/lib/router"
import { invoke } from "@/lib/utils/functional"

interface AuthRefreshProps {
  presentCookies?: string[]
}

/**
 * Shared component that handles auth token refresh.
 * Used by both /auth/refresh page and _app.tsx when needsRefresh is true.
 */
export default function AuthRefresh({ presentCookies }: AuthRefreshProps) {
  const init = useRef(false)
  const router = useRouter()

  useEffect(() => {
    if (!init.current) {
      init.current = true

      // Check for redirect loop
      const loopCheck = authLoopDetector.checkAndIncrement("refresh", {
        referer: window.document.referrer,
        presentCookies,
      })

      if (loopCheck.reason === "capability") {
        nProgress.done()
        return
      }

      if (loopCheck.reason === "redirect-loop") {
        nProgress.done()
        mtLocation.replace("/check-settings")
        return
      }

      nProgress.start()
      invoke(async () => {
        try {
          // Compute callback URL before refresh in case we need it for error handling
          const currentUrl = mtLocation.href
          const errorCallbackUrl = containsAuthPath(currentUrl)
            ? "/"
            : currentUrl

          await refreshAuthToken(errorCallbackUrl)
          // Reload the current page after successful refresh
          router.reload()
        } catch (error) {
          // handleRefreshFailure already handles sign-out and redirect
          console.error("[AuthRefresh] Failed to refresh token:", error)
        }
      })
    }
  }, [presentCookies, router])

  return null // No UI needed, just handling the refresh
}
